import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../design/Button';
import ErrorState from '../design/ErrorState';
import WaveformPlayer from './WaveformPlayer';
import AttemptResultCard from './AttemptResultCard';
import useRecorder, { blobToBase64 } from '../utils/useRecorder';
import { convertAudioBlobToMp3 } from '../../app/utils/mp3Converter';
import { AttemptResult, SchoolApiError, submitAttempt } from '../utils/schoolApi';

interface RecorderPanelProps {
  /** Called after an attempt was saved, so the page can refresh progress/history. */
  onAttemptSaved: () => void;
}

/** Record → listen to yourself → compare. (The rabbi recording plays from PasukCard.) */
const RecorderPanel: React.FC<RecorderPanelProps> = ({ onAttemptSaved }) => {
  const { t, i18n } = useTranslation();
  const recorder = useRecorder();
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const startRecording = async (): Promise<void> => {
    setResult(null);
    setErrorKey(null);
    await recorder.start();
  };

  const compare = async (): Promise<void> => {
    if (!recorder.blob) return;
    setComparing(true);
    setErrorKey(null);
    try {
      const mp3 = await convertAudioBlobToMp3(recorder.blob);
      const base64 = await blobToBase64(mp3);
      const attempt = await submitAttempt(base64, i18n.language);
      setResult(attempt);
      if (attempt.saved) onAttemptSaved();
    } catch (err) {
      console.error('[school] submitAttempt failed', err);
      const code = err instanceof SchoolApiError ? err.code : 'server_error';
      setErrorKey(
        code === 'rabbi_audio_missing' || code === 'no_current_pasuk'
          ? `school_error_${code}`
          : 'school_error_compare',
      );
    } finally {
      setComparing(false);
    }
  };

  const isRecording = recorder.state === 'recording';

  return (
    <div>
      <div className="grid gap-3">
        {isRecording ? (
          <Button variant="danger" size="lg" icon="stop" onClick={recorder.stop}>
            {t('school_stop_recording')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="lg"
            icon="mic"
            onClick={startRecording}
            disabled={comparing}
          >
            {recorder.state === 'recorded' ? t('school_record_again') : t('school_record')}
          </Button>
        )}
      </div>

      {isRecording && (
        <p className="mt-3 flex items-center justify-center gap-2 text-sm font-medium text-school-danger">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-school-danger" />
          {t('school_recording_now')}
        </p>
      )}

      {recorder.error && (
        <p className="mt-3 text-center text-sm text-school-danger" role="alert">
          {t(
            recorder.error === 'mic_denied'
              ? 'school_error_mic_denied'
              : 'school_error_mic_unsupported',
          )}
        </p>
      )}

      {recorder.state === 'recorded' && recorder.url && (
        <div className="mt-4 grid gap-3">
          <p className="text-sm font-semibold text-school-ink">{t('school_my_recording')}</p>
          <WaveformPlayer src={recorder.url} label={t('school_play')} bars={56} />
          <Button variant="primary" size="lg" icon="compare" onClick={compare} loading={comparing}>
            {comparing ? t('school_comparing') : t('school_compare')}
          </Button>
        </div>
      )}

      {errorKey && (
        <div className="mt-4">
          <ErrorState message={t(errorKey)} />
        </div>
      )}
      {result && (
        <AttemptResultCard
          score={result.score}
          explanation={result.explanation}
          saved={result.saved}
        />
      )}
    </div>
  );
};

export default RecorderPanel;
